import { Loading, TextInput } from 'carbon-components-react';
import { ConfirmModal } from '../../components/modal/confirm-modal/ConfirmModal.component';
import { useReducerSpread } from '../../utils/common.util';
import CustomButton from '../../components/custom-button/custom-btn.component';
import { useState } from 'react';
import { RenderIf } from '../../components/RenderIf';
import useSWR from 'swr';
import { useCustomMutation } from '../../utils/useCustomMutation';
import { postMethod } from '../../api/common';
import ComponentGuard from '../../components/component-guard/ComponentGuard';

const initialData = {
  classPercentage: null,
  examPercentage: null,
};

export default function PercentagesSetting({ isOpen, closeModal }) {
  const [formData, setFormData] = useReducerSpread(initialData);
  const [isChange, setIsChange] = useState(false);

  const { data, isLoading } = useSWR(
    isOpen ? 'academic/grading/retrieve-grading-percentage' : null,
  );
  const percentages = data?.response;

  useState(() => {
    if (percentages) {
      setFormData({
        classPercentage: percentages.classPercentage,
        examPercentage: percentages.examPercentage,
      });
    }
  }, [percentages]);

  const examPercentage = percentages?.examPercentage ?? 50;
  const classPercentage = percentages?.classPercentage ?? 50;

  const { trigger, error, isMutating } = useCustomMutation(
    'academic/grading/create-or-update-grading-percentage',
    (url, { arg }) => postMethod(url, arg),
  );

  function handleSubmit() {
    if (!isChange) {
      setIsChange(true);
      return;
    }

    trigger(formData, {
      onSuccess: () => {
        setIsChange(false);
        closeModal();
      },
    });
  }

  return (
    <ConfirmModal
      title="Grading Percentages"
      isOpen={isOpen}
      closeModal={() => {
        closeModal();
        setIsChange(false);
      }}
      secondaryActionText="Close"
      secondaryBtnClasses="secondary"
      description={
        <>
          <div
            style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}
          >
            <span>
              You can change grading percentages for end of term grading. The
              default percentages are 50% for class and 50% for exam.
            </span>
            <span>
              If your school is currently using 50%/50% grading, you do not need
              to make any changes.
            </span>
          </div>
          <div className="mt-30 d-flex-space-between" style={{ gap: '20px' }}>
            <RenderIf condition={isLoading}>
              <Loading small withOverlay={false} />
            </RenderIf>
            <RenderIf condition={!isLoading}>
              <div style={{}}>
                <div className="mb-20">
                  <span>Exam Percentage:</span> <br />{' '}
                  <RenderIf condition={!isChange}>
                    <span>{examPercentage}%</span>
                  </RenderIf>{' '}
                  <RenderIf condition={isChange}>
                    <TextInput
                      labelText=""
                      placeholder="Enter Exam Percentage"
                      type="number"
                      value={formData.examPercentage}
                      onChange={(e) => {
                        let value = Number(e.target.value);
                        if (value > 100) value = 100;
                        if (value < 0) value = 0;
                        setFormData({
                          examPercentage: value,
                          classPercentage: 100 - value,
                        });
                      }}
                    />
                  </RenderIf>
                </div>
                <div>
                  <span>Class Percentage:</span> <br />{' '}
                  <RenderIf condition={!isChange}>
                    <span>{classPercentage}%</span>
                  </RenderIf>{' '}
                  <RenderIf condition={isChange}>
                    <TextInput
                      labelText=""
                      placeholder="Enter Class Percentage"
                      type="number"
                      value={formData.classPercentage}
                      onChange={(e) => {
                        let value = Number(e.target.value);
                        if (value > 100) value = 100;
                        if (value < 0) value = 0;
                        setFormData({
                          classPercentage: value,
                          examPercentage: 100 - value,
                        });
                      }}
                    />
                  </RenderIf>
                </div>
              </div>
            </RenderIf>
          </div>

          <ComponentGuard allowedRoles={['AcademicReportPercentagesAccess']}>
            <RenderIf condition={!isLoading}>
              <div style={{ display: 'flex', justifyContent: 'flex-end' }}>
                <CustomButton
                  classes="primary font-bold"
                  style={{ justifySelf: 'end', marginTop: '30px' }}
                  isLoading={isMutating}
                  onClick={handleSubmit}
                >
                  {isChange ? 'SAVE' : 'CHANGE'}
                </CustomButton>
              </div>
            </RenderIf>
          </ComponentGuard>
        </>
      }
      errorMessage={error ? error.message : null}
    />
  );
}
